import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, MapPin, ExternalLink, Loader2, Clock, History } from "lucide-react";
import SEO from "../components/SEO";
import { parseLocalDate, formatDate, formatDateRange } from "../lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if festival is past (based on end_date or start_date)
const isPastFestival = (festival) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const festivalEndDate = parseLocalDate(festival.end_date || festival.start_date);
  festivalEndDate.setHours(23, 59, 59, 999);
  return festivalEndDate < today;
};

export default function Festivals() {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("upcoming");

  useEffect(() => {
    fetchFestivals();
  }, []);

  const fetchFestivals = async () => {
    try {
      const response = await axios.get(`${API}/festivals`);
      setFestivals(response.data);
    } catch (error) {
      console.error("Error fetching festivals:", error);
    } finally {
      setLoading(false);
    }
  };

  // Split festivals into upcoming and past
  const upcomingFestivals = festivals
    .filter(f => !isPastFestival(f))
    .sort((a, b) => parseLocalDate(a.start_date) - parseLocalDate(b.start_date));
  
  const pastFestivals = festivals
    .filter(f => isPastFestival(f))
    .sort((a, b) => parseLocalDate(b.start_date) - parseLocalDate(a.start_date)); // Most recent first

  const displayedFestivals = activeTab === "upcoming" ? upcomingFestivals : pastFestivals;

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="AI Film Festivals"
        description="Discover AI film festivals worldwide. View dates, locations, and submission deadlines."
        url="https://aifilmsociety.org/festivals"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Global Calendar
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            AI Film Festivals
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Discover AI film festivals around the world — view dates, locations, and 
            submission deadlines.
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10">
          <button
            onClick={() => setActiveTab("upcoming")}
            data-testid="tab-upcoming"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "upcoming"
                ? "text-purple-light border-b-2 border-purple"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <Clock className="w-4 h-4" />
            Upcoming
            {upcomingFestivals.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-purple/20 text-purple-light rounded-full">
                {upcomingFestivals.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("past")}
            data-testid="tab-past"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === "past"
                ? "text-purple-light border-b-2 border-purple"
                : "text-slate-400 hover:text-white"
            }`}
          >
            <History className="w-4 h-4" />
            Past
            {pastFestivals.length > 0 && (
              <span className="ml-1 px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded-full">
                {pastFestivals.length}
              </span>
            )}
          </button>
        </div>

        {/* Festivals List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : displayedFestivals.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400">
              {activeTab === "upcoming" 
                ? "No upcoming festivals. Check back soon!" 
                : "No past festivals to display."}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {displayedFestivals.map((festival, index) => (
              <motion.div
                key={festival.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/festivals/${festival.id}`}
                  data-testid={`festival-${index}`}
                  className={`block bg-void-surface border border-white/5 hover:border-purple/30 transition-all duration-500 group overflow-hidden ${
                    activeTab === "past" ? "opacity-80 hover:opacity-100" : ""
                  }`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image (if available) */}
                    {festival.image_url && (
                      <div className="md:w-48 md:flex-shrink-0">
                        <img 
                          src={festival.image_url} 
                          alt={festival.name}
                          className="w-full h-40 md:h-full object-cover"
                        />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-6 md:p-8 flex-1">
                      <div className="flex flex-wrap items-center gap-4 mb-3">
                        <span className={`flex items-center gap-2 text-sm font-medium ${
                          activeTab === "past" ? "text-slate-400" : "text-purple-light"
                        }`}>
                          <Calendar className="w-4 h-4" />
                          {formatDateRange(festival.start_date, festival.end_date)}
                        </span>
                        <span className="flex items-center gap-2 text-sm text-slate-400">
                          <MapPin className="w-4 h-4" />
                          {festival.location}
                        </span>
                        {activeTab === "past" && (
                          <span className="text-xs px-2 py-1 bg-slate-700/50 text-slate-400 uppercase tracking-wider">
                            Past Event
                          </span>
                        )}
                      </div>
                      <h2 className="font-syne font-bold text-white text-xl md:text-2xl mb-3 group-hover:text-purple-light transition-colors">
                        {festival.name}
                      </h2>
                      <p className="text-slate-400 text-sm md:text-base mb-4 whitespace-pre-line line-clamp-3">
                        {festival.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-4">
                        {festival.submission_deadline && activeTab === "upcoming" && (
                          <p className="text-sm text-purple-light">
                            Submission Deadline: {formatDate(festival.submission_deadline)}
                          </p>
                        )}
                        {festival.coverage && activeTab === "past" && (
                          <p className="text-sm text-slate-500">
                            Coverage available
                          </p>
                        )}
                        {festival.website_url && (
                          <span className="inline-flex items-center gap-1 text-sm text-slate-500 group-hover:text-purple-light transition-colors">
                            <ExternalLink className="w-3 h-3" />
                            Visit Website
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
